import get from 'lodash/get'
import { Chunk, FindChunks } from 'react-highlight-words'
import { toast } from 'react-toastify'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { addAllStepOfProcessToCommonLibrary } from 'API/commonLibrary'
import { getUserPermissionsOnProcesses, getUserPermissionsOnCollections } from 'API/user'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IGroupMember } from 'interfaces/group'
import { checkValidArray } from 'utils/common'

// *INFO: A custom findChunks function that returns chunks for whole words only
export const findFullWordChunks: (options: FindChunks) => Chunk[] = ({ searchWords, textToHighlight }) => {
  // *INFO: An array to store chunks of the text that will be highlighted
  const chunks: Chunk[] = []
  const textInLowerCase: string = textToHighlight.toLowerCase()

  // *INFO: A regular expression that matches word boundaries
  const wordBoundaryRegExp: RegExp = /\b/

  // *INFO: An array of words in the lowercase text
  const individualWordsInText: string[] = textInLowerCase.split(wordBoundaryRegExp)

  let startingIndexTracker: number = 0

  // *INFO: An array of objects storing the word, its starting index, and its length
  const individualWordsWithPositionAndLength: {
    word: string
    index: number
    length: number
  }[] = individualWordsInText.map(individualWord => {
    const indexOfWordInText: number = textInLowerCase.indexOf(individualWord, startingIndexTracker)
    startingIndexTracker = indexOfWordInText
    return {
      word: individualWord,
      index: indexOfWordInText,
      length: individualWord.length
    }
  })

  // *INFO: Loop through each word in the searchWords array
  searchWords
    .join(' ')
    .split(wordBoundaryRegExp)
    .forEach(searchWord => {
      // *INFO: Lowercase version of the current search word
      const searchWordInLowerCase: string = String(searchWord).toLowerCase()

      // *INFO: Loop through each individual word in the text with its position and length
      individualWordsWithPositionAndLength.forEach(individualWordWithPositionAndLength => {
        // *INFO: If the current individual word is equal to the current search word
        if (individualWordWithPositionAndLength.word === searchWordInLowerCase) {
          // *INFO: Calculate the start and end indices of the current individual word
          const startIndex: number = individualWordWithPositionAndLength.index
          const endIndex: number =
            individualWordWithPositionAndLength.index + individualWordWithPositionAndLength.length

          // *INFO: Push a chunk for the current individual word to the chunks array
          chunks.push({
            start: startIndex,
            end: endIndex
          })
        }
      })
    })

  // *INFO: Return the final chunks array
  return chunks
}

function checkUserPermissions(userPermissions: IGroupMember[]) {
  return userPermissions?.some(userPermission => userPermission?.permissionId === IGroupMemberPermissionId.EDITOR)
}

export async function isUserHavePermissionForProcess(userId: number, isBasicMember: boolean, processId: number) {
  if (!isBasicMember) return { accessPermission: true, editorPermission: true }
  const data = await getUserPermissionsOnProcesses(userId, [processId])

  return {
    accessPermission: checkValidArray(data),
    editorPermission: checkUserPermissions(get(data, '[0].userPermissions', []) as IGroupMember[])
  }
}

export async function isUserHavePermissionForCollection(userId: number, isBasicMember: boolean, collectionId: number) {
  if (!isBasicMember) return true
  const data = await getUserPermissionsOnCollections(userId, [collectionId])
  return checkUserPermissions(get(data, '[0].userPermissions', []) as IGroupMember[])
}

export async function updateViewOnlyItemsAfterSelectAll(
  userId: number,
  items: IEsGlobalSearchItem[],
  setViewOnlyProcess: (viewOnlyProcessIds: number[]) => void,
  isBasicUser: boolean = false
) {
  const processIds: number[] = []
  const collectionIds: number[] = []
  setViewOnlyProcess([])

  items.forEach(item => {
    if (item.collectionName === EEsGlobalSearchCollection.PROCESS) {
      processIds.push(item.correlatingId)
    } else {
      collectionIds.push(item.correlatingId)
    }
  })

  if (isBasicUser) {
    const [processData] = await Promise.all([getUserPermissionsOnProcesses(userId, processIds)])

    setViewOnlyProcess(
      processData
        .map(permission => {
          if (!checkUserPermissions(permission?.userPermissions ?? [])) {
            return permission?.id
          }
          return 0
        })
        .filter(Boolean)
    )
  }

  // TODO: Integrate later
  // const viewableCollectionId = collectionData.map(collection => collection?.id)
  // setViewOnlyCollection(difference(collectionIds, viewableCollectionId).filter(Boolean))
}

export async function addAllStepsOfProcessToCommonLibrary(
  processId: number,
  closeModal: () => void,
  refetch: () => void
) {
  if (!processId) return
  try {
    await addAllStepOfProcessToCommonLibrary(processId)
    refetch()
    closeModal()
    toast.success('Added all steps to common library')
  } catch (error: any) {
    toast.error(`${error}`)
  }
}

export function mapTitleToValueSearchOrder(value: string) {
  switch (value) {
    case 'Last updated':
      return 'updatedAt desc'
    case 'Most relevant':
      return 'relevant'
    case 'Most viewed':
      return 'view desc'
    case 'Newest':
      return 'publishedDate desc'
    case 'Name (A-Z)':
      return 'name.keyword asc'
    default:
      return 'Most relevant'
  }
}
